import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { IssueSslRequest, SslCertificateDto } from "@/types/api";

/**
 * SSL cert state for a container. Issuance is async (LE takes 30s–5min);
 * the React Query refetchInterval kicks in whenever there's a pending
 * cert so the UI flips to "active" without the user clicking refresh.
 */
export function useSslCertificates(containerId: number | undefined) {
  return useQuery({
    queryKey: ["ssl", containerId],
    enabled: typeof containerId === "number",
    queryFn: ({ signal }) =>
      api.get<SslCertificateDto[]>(`/api/containers/${containerId}/ssl`, signal),
    // While at least one cert is pending, refetch every 15s — once they
    // all settle, fall back to "no polling" until the user does something.
    refetchInterval: query => {
      const certs = query.state.data as SslCertificateDto[] | undefined;
      return certs?.some(c => c.status === "pending") ? 15_000 : false;
    },
    staleTime: 10_000
  });
}

export function useIssueSsl(containerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: IssueSslRequest) =>
      api.post<SslCertificateDto>(`/api/containers/${containerId}/ssl/issue`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ssl", containerId] });
      qc.invalidateQueries({ queryKey: ["containers", containerId] });
      qc.invalidateQueries({ queryKey: ["accounts", "me"] });
    }
  });
}

export function useRenewSsl(containerId: number) {
  const qc = useQueryClient();
  return useMutation({
    // providerReference contains '|' — split path so React Router doesn't
    // mangle it; backend has the {*reference} catch-all to compensate.
    mutationFn: (reference: string) =>
      api.post<SslCertificateDto>(`/api/containers/${containerId}/ssl/${encodeURIComponent(reference)}/renew`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ssl", containerId] });
    }
  });
}

export function useRevokeSsl(containerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reference: string) =>
      api.delete(`/api/containers/${containerId}/ssl/${encodeURIComponent(reference)}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ssl", containerId] });
      qc.invalidateQueries({ queryKey: ["containers", containerId] });
      qc.invalidateQueries({ queryKey: ["accounts", "me"] });
    }
  });
}
